import { Decimal } from 'decimal.js';
import { ethers } from 'ethers';
import type {
  CryptoAssetMaxSendAmountParams,
  CryptoAssetMaxSendAmountResult,
  CryptoAssetTransactionHistoryItem,
  CryptoAssetTransactionHistoryParams,
  CryptoAssetTransactionHistoryResult,
} from './ICryptoAssetProvider';
import type { CryptoAsset } from '@/types/WalletTypes';
import type { ExplorerTransaction, WalletServiceClient } from '@/resources/wallet-service.client';

export type ExplorerTransactionHistoryAction = 'tokentx' | 'txlist';

const DEFAULT_GAS_BUFFER_PERCENT = 20;

export const bufferGasValue = (value: ethers.BigNumber, bufferPercent = DEFAULT_GAS_BUFFER_PERCENT) => {
  const buffer = value.mul(bufferPercent).div(100);
  return value.add(buffer);
};

export const emptyTransactionHistory = (page: number): CryptoAssetTransactionHistoryResult => ({
  page,
  totalElements: 0,
  totalPages: 0,
  transactions: [],
});

export const buildExplorerTransactionHistoryUrl = ({
  action,
  address,
  contractAddress,
  explorerApiUrl,
  page,
  pageSize,
  sortDir,
}: {
  action: ExplorerTransactionHistoryAction;
  address: string;
  contractAddress?: string | null;
  explorerApiUrl: string;
  page: number;
  pageSize: number;
  sortDir?: string | null;
}) => {
  const [urlWithoutHash, hash] = explorerApiUrl.split('#');
  const [baseUrl, existingQueryString] = urlWithoutHash.split('?');
  const queryParams = new URLSearchParams(existingQueryString);

  queryParams.set('module', 'account');
  queryParams.set('action', action);
  queryParams.set('address', address);
  queryParams.set('offset', String(pageSize));
  queryParams.set('page', String(page + 1));
  queryParams.set('sort', sortDir?.toLowerCase() === 'asc' ? 'asc' : 'desc');

  if (action === 'tokentx' && contractAddress) {
    queryParams.set('contractaddress', contractAddress);
  }
  return `${baseUrl}?${queryParams}${hash ? `#${hash}` : ''}`;
};

export const getExplorerTransactionHistory = async (
  params: CryptoAssetTransactionHistoryParams & {
    action: ExplorerTransactionHistoryAction;
    filterTransaction?: (transaction: ExplorerTransaction) => boolean;
  }
): Promise<CryptoAssetTransactionHistoryResult> => {
  const explorerApiUrl = params.cryptoAsset.asset.blockchainConfiguration.blockExplorerApiUrl;
  if (!explorerApiUrl) {
    return emptyTransactionHistory(params.page);
  }
  const response = await fetch(
    buildExplorerTransactionHistoryUrl({
      action: params.action,
      address: params.address,
      contractAddress: params.cryptoAsset.asset.definitionAddress,
      explorerApiUrl,
      page: params.page,
      pageSize: params.pageSize,
      sortDir: params.sortDir,
    })
  );
  const data = (await response.json()) as { result?: ExplorerTransaction[] };
  const explorerTransactions = params.filterTransaction
    ? (data.result ?? []).filter(params.filterTransaction)
    : (data.result ?? []);
  const transactions = normalizeExplorerTransactions(params, explorerTransactions);
  const startIdx = params.page * params.pageSize;

  return {
    page: params.page,
    totalElements: transactions.length,
    totalPages: Math.ceil(transactions.length / params.pageSize),
    transactions: transactions.slice(startIdx, startIdx + params.pageSize),
  };
};

export const getWalletServiceTransactionHistory = async (
  params: CryptoAssetTransactionHistoryParams & {
    walletServiceClient: WalletServiceClient;
  }
): Promise<CryptoAssetTransactionHistoryResult> => {
  const data = await params.walletServiceClient.getTransactionHistory({
    core: params.core,
    page: params.page,
    pageSize: params.pageSize,
    sortBy: params.sortBy,
    sortDir: params.sortDir,
    symbol: params.cryptoAsset.asset.canonicalName,
  });
  const wallet = data?.wallet?.[0];
  if (!wallet?.transactions?.length) {
    return emptyTransactionHistory(params.page);
  }
  return {
    page: params.page,
    totalElements: wallet.totalRecords ?? wallet.transactions.length,
    totalPages: wallet.totalPages ?? Math.ceil(wallet.transactions.length / params.pageSize),
    transactions: wallet.transactions as CryptoAssetTransactionHistoryItem[],
  };
};

const normalizeExplorerTransactions = (
  params: CryptoAssetTransactionHistoryParams,
  transactions: ExplorerTransaction[]
): CryptoAssetTransactionHistoryItem[] => {
  const blockExplorerUrl = params.cryptoAsset.asset.blockchainConfiguration.blockExplorerUrl?.replace(/\/$/, '');
  const tokenDecimals = params.cryptoAsset.display.decimalPrecision;
  const feeDecimals = params.cryptoAsset.fee.decimalPrecision;

  return transactions.map((txn) => {
    const from = txn.from || '';
    const to = txn.to || '';
    const hash = txn.hash || '';
    const isWithdrawal = from.toLowerCase() === params.address.toLowerCase();
    const amount = Number(txn.value || 0) / 10 ** Number(txn.tokenDecimal ?? tokenDecimals);
    const fee = (Number(txn.gasUsed || 0) * Number(txn.gasPrice || 0)) / 10 ** feeDecimals;

    return {
      id: hash,
      status: txn.isError === '1' || txn.txreceipt_status === '0' ? 'Failed' : 'Complete',
      confirmations: txn.confirmations,
      timestamp: new Date(Number(txn.timeStamp || 0) * 1000).toISOString(),
      fee,
      link: blockExplorerUrl ? `${blockExplorerUrl}/tx/${hash}` : '',
      to: to ? [to] : [],
      from,
      amount: isWithdrawal ? -amount : amount,
      type: isWithdrawal ? 'Withdrawal' : 'Deposit',
      total: isWithdrawal ? -amount : amount,
    };
  });
};

export const createEthersRpcProviderResolver = () => {
  const rpcProvidersByChainId = new Map<number, ethers.providers.JsonRpcProvider>();

  return (cryptoAsset: CryptoAsset): ethers.providers.JsonRpcProvider => {
    const { name, rpcUrl } = cryptoAsset.asset.blockchainConfiguration;
    const chainId = requireCryptoAssetChainId(cryptoAsset);
    const existingProvider = rpcProvidersByChainId.get(chainId);
    if (existingProvider) {
      return existingProvider;
    }
    const rpcProvider = new ethers.providers.JsonRpcProvider(rpcUrl, {
      chainId,
      name,
    });
    rpcProvidersByChainId.set(chainId, rpcProvider);
    return rpcProvider;
  };
};

export const requireCryptoAssetChainId = (cryptoAsset: CryptoAsset) => {
  const { chainId } = cryptoAsset.asset.blockchainConfiguration;
  if (typeof chainId !== 'number') {
    throw new Error(`Chain ID is required for ${cryptoAsset.uniqueKey}`);
  }
  return chainId;
};

export const requireMnemonic = (mnemonic: string | undefined, transactionType?: string) => {
  if (!mnemonic) {
    throw new Error(transactionType ? `Mnemonic is required for ${transactionType}` : 'Mnemonic is required');
  }
  return mnemonic;
};

export const getStandardMaxSendAmount = ({
  coin,
  precision,
}: CryptoAssetMaxSendAmountParams): CryptoAssetMaxSendAmountResult => {
  const maxCoin = new Decimal(coin.coinAmt).toFixed(precision);
  const priceUsd = requireValidPrice(coin.priceUsd);
  return {
    maxCoin,
    maxFiat: priceUsd ? toFiat({ amount: maxCoin, conversion: priceUsd, precision: 2 }) : null,
  };
};

export const getSameFeeAssetMaxSendAmount = ({
  coin,
  fee,
  precision,
}: CryptoAssetMaxSendAmountParams): CryptoAssetMaxSendAmountResult => {
  const maxCoin = new Decimal(coin.coinAmt).minus(new Decimal(fee?.coin || 0)).toFixed(precision);
  const priceUsd = requireValidPrice(coin.priceUsd);
  return {
    maxCoin,
    maxFiat: priceUsd ? toFiat({ amount: maxCoin, conversion: priceUsd, precision: 2 }) : null,
  };
};

const requireValidPrice = (price: string | number | null | undefined) => (Number(price) > 0 ? price : null);

const toFiat = ({
  amount,
  conversion,
  precision,
}: {
  amount: string;
  conversion: string | number;
  precision: number;
}) => Decimal.mul(amount, conversion).toFixed(precision);
